import { useEffect, useState } from "react";
import { events as eventsApi } from "@v0y4ge/api-client";
import type { UIEvent } from "../../../lib/events-adapter";

export interface EventTicket {
  id: string; name: string; desc: string; price: number;
  sold: number; capacity: number; currency: string;
}

function toTicket(t: Record<string, any>): EventTicket {
  return {
    id: String(t.id ?? ""),
    name: String(t.name ?? t.tierName ?? "Ticket"),
    desc: String(t.description ?? t.desc ?? ""),
    price: Number(t.price ?? t.priceMinor != null ? (t.priceMinor / 100) : 0),
    sold: Number(t.sold ?? t.registeredCount ?? 0),
    capacity: Number(t.capacity ?? t.totalCapacity ?? 0),
    currency: String(t.currency ?? "USD"),
  };
}

/**
 * useEventTickets — fetches per-event ticket tiers from @v0y4ge/api-client.
 * No synthesis. Renders empty until the client returns.
 */
export function useEventTickets(ev: UIEvent | null | undefined) {
  const [tickets, setTickets] = useState<EventTicket[] | null>(null);
  const id = ev?.id;
  useEffect(() => {
    if (!id) { setTickets([]); return; }
    let live = true;
    setTickets(null);
    eventsApi.listEventTickets(id).then((r: any) => {
      if (!live) return;
      const arr = r.tickets ?? r.tiers ?? r.data ?? [];
      setTickets(arr.map(toTicket));
    }).catch(() => { if (live) setTickets([]); });
    return () => { live = false; };
  }, [id]);
  return tickets ?? [];
}

/** Backwards-compat shim for any call-sites still using getEventTickets(ev) synchronously.
 *  Returns empty array until replaced with the useEventTickets hook. */
export function getEventTickets(_ev: UIEvent | null | undefined): EventTicket[] {
  return [];
}
